import React, { useState, useEffect } from 'react';
import { 
  Palette, 
  Key, 
  Bell, 
  Shield, 
  Download, 
  Trash2,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../context/AuthContext';

const Settings: React.FC = () => {
  const { colors, currentTheme, setTheme, themes } = useTheme();
  const { user, updateUser } = useAuth();
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    desktop: true,
    reminders: true
  });
  const [autoSave, setAutoSave] = useState(user?.settings.autoSave ?? true);
  const [showPreview, setShowPreview] = useState(user?.settings.showPreview ?? true);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(currentTheme);

  // Initialize settings from user data
  useEffect(() => {
    if (user?.settings) {
      setApiKey(user.settings.deepseekApiKey || '');
      setAutoSave(user.settings.autoSave ?? true);
      setShowPreview(user.settings.showPreview ?? true);
      setSelectedTheme(user.settings.theme || 'latte');
    }
  }, [user]);

  // Update selectedTheme when currentTheme changes
  useEffect(() => {
    setSelectedTheme(currentTheme);
  }, [currentTheme]);

  // Track changes to enable save button
  useEffect(() => {
    const currentSettings = {
      theme: selectedTheme,
      autoSave,
      showPreview,
      apiKey
    };
    
    const originalSettings = {
      theme: user?.settings.theme || 'latte',
      autoSave: user?.settings.autoSave ?? true,
      showPreview: user?.settings.showPreview ?? true,
      apiKey: user?.settings.deepseekApiKey || ''
    };
    
    const changed = JSON.stringify(currentSettings) !== JSON.stringify(originalSettings);
    setHasChanges(changed);
  }, [selectedTheme, autoSave, showPreview, apiKey, user]);

  const handleSaveSettings = () => {
    // Apply the theme immediately
    setTheme(selectedTheme);
    
    // Update user settings
    updateUser({
      settings: {
        ...user?.settings,
        theme: selectedTheme,
        deepseekApiKey: apiKey,
        autoSave,
        showPreview
      }
    });
    
    setHasChanges(false);
  };

  const handleExportData = () => {
    // Simulate data export
    const data = {
      notes: [],
      projects: [],
      documents: [],
      settings: user?.settings
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'enki-data.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteAccount = () => {
    if (confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      // Handle account deletion
      console.log('Account deletion requested');
    }
  };

  const handleThemeChange = (themeKey: string) => {
    setSelectedTheme(themeKey as any);
    // Apply theme immediately for instant preview
    setTheme(themeKey as any);
  };

  return (
    <div className="space-y-6" style={{ backgroundColor: colors.base, color: colors.text }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
          Settings
        </h1>
        
        <button 
          onClick={handleSaveSettings}
          disabled={!hasChanges}
          className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
          style={{ backgroundColor: hasChanges ? colors.blue : colors.surface0 }}
        >
          <Save className="w-4 h-4" />
          {hasChanges ? 'Save Changes' : 'No Changes'}
        </button>
      </div>

      {/* Theme Settings */}
      <div 
        className="p-6 rounded-xl border transition-colors duration-300"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          <Palette className="w-6 h-6" style={{ color: colors.blue }} />
          <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
            Appearance
          </h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
              Theme
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(themes).map(([key, theme]) => (
                <button
                  key={key}
                  onClick={() => handleThemeChange(key)}
                  className={`p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                    selectedTheme === key ? 'ring-2' : ''
                  }`}
                  style={{ 
                    backgroundColor: theme.colors.base,
                    borderColor: selectedTheme === key ? colors.blue : theme.colors.surface0,
                    ringColor: colors.blue
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: theme.colors.blue }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: theme.colors.green }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: theme.colors.yellow }}
                    />
                  </div>
                  <p className="text-sm font-medium" style={{ color: theme.colors.text }}>
                    {theme.name}
                  </p>
                  {selectedTheme === key && (
                    <div className="mt-2 text-xs" style={{ color: colors.blue }}>
                      ✓ Selected
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI Settings */}
      <div 
        className="p-6 rounded-xl border transition-colors duration-300"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          <Key className="w-6 h-6" style={{ color: colors.blue }} />
          <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
            AI Configuration
          </h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              DeepSeek API Key
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your DeepSeek API key"
                className="w-full px-3 py-2 pr-10 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
                style={{
                  backgroundColor: colors.base,
                  borderColor: colors.surface1,
                  color: colors.text
                }}
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                style={{ color: colors.subtext0 }}
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs mt-1" style={{ color: colors.subtext0 }}>
              Your API key is encrypted and stored securely. Get your key from{' '}
              <a 
                href="https://platform.deepseek.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: colors.blue }}
              >
                DeepSeek Platform
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Editor Settings */}
      <div 
        className="p-6 rounded-xl border transition-colors duration-300"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-6 h-6" style={{ color: colors.blue }} />
          <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
            Editor Preferences
          </h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium" style={{ color: colors.text }}>
                Auto-save notes
              </label>
              <p className="text-xs" style={{ color: colors.subtext0 }}>
                Automatically save changes as you type
              </p>
            </div>
            <button
              onClick={() => setAutoSave(!autoSave)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoSave ? 'bg-blue-600' : 'bg-gray-200'
              }`}
              style={{ backgroundColor: autoSave ? colors.blue : colors.surface1 }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoSave ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium" style={{ color: colors.text }}>
                Show preview pane
              </label>
              <p className="text-xs" style={{ color: colors.subtext0 }}>
                Display markdown preview alongside editor
              </p>
            </div>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                showPreview ? 'bg-blue-600' : 'bg-gray-200'
              }`}
              style={{ backgroundColor: showPreview ? colors.blue : colors.surface1 }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showPreview ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div 
        className="p-6 rounded-xl border transition-colors duration-300"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          <Bell className="w-6 h-6" style={{ color: colors.blue }} />
          <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
            Notifications
          </h2>
        </div>
        
        <div className="space-y-4">
          {Object.entries(notifications).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium capitalize" style={{ color: colors.text }}>
                  {key === 'push' ? 'Push notifications' : `${key} notifications`}
                </label>
                <p className="text-xs" style={{ color: colors.subtext0 }}>
                  {key === 'email' && 'Receive updates via email'}
                  {key === 'push' && 'Mobile push notifications'}
                  {key === 'desktop' && 'Browser notifications'}
                  {key === 'reminders' && 'Study reminders and deadlines'}
                </p>
              </div>
              <button
                onClick={() => setNotifications(prev => ({ ...prev, [key]: !value }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  value ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                style={{ backgroundColor: value ? colors.blue : colors.surface1 }}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Data Management */}
      <div 
        className="p-6 rounded-xl border transition-colors duration-300"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          <Download className="w-6 h-6" style={{ color: colors.blue }} />
          <h2 className="text-xl font-semibold" style={{ color: colors.text }}>
            Data Management
          </h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium" style={{ color: colors.text }}>
                Export Data
              </label>
              <p className="text-xs" style={{ color: colors.subtext0 }}>
                Download all your notes, projects, and documents
              </p>
            </div>
            <button
              onClick={handleExportData}
              className="px-4 py-2 rounded-lg border font-medium transition-all hover:opacity-80"
              style={{ 
                backgroundColor: colors.base,
                borderColor: colors.surface1,
                color: colors.text
              }}
            >
              Export
            </button>
          </div>

          <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: colors.surface1 }}>
            <div>
              <label className="text-sm font-medium" style={{ color: colors.red }}>
                Delete Account
              </label>
              <p className="text-xs" style={{ color: colors.subtext0 }}>
                Permanently delete your account and all data
              </p>
            </div>
            <button
              onClick={handleDeleteAccount}
              className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
              style={{ backgroundColor: colors.red }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;